import {
  AI_ASSISTANT_CHATKIT_URL,
  AI_ASSISTANT_DEFAULT_URL,
  BrowserStorage,
  aiAssistantUrlCookieName,
  isDevelopment,
  isLocalDevServer,
} from '@const/consts';
import TokensService from '@services/tokensService';
import cookies from '@utils/cookies';
import { v4 as uuidv4 } from 'uuid';

import {
  IAiAssistantChatPayload,
  IAiAssistantHistoryMessage,
  IAiAssistantHistoryParams,
  IAiAssistantStreamEvent,
  IAiAssistantStreamParams,
  IAiAssistantThread,
} from './types';

const CLIENT_PLATFORM = 'web';
const APP_VERSION = process.env.REACT_APP_VERSION || '';

export const streamAiAssistantChat = async ({
  threadId,
  orgOguid,
  userOguid,
  messageId,
  correlationId,
  text,
  toolCall,
  signal,
  locale,
  timezone,
  onEvent,
}: IAiAssistantStreamParams): Promise<void> => {
  const payload: IAiAssistantChatPayload = {
    threadId,
    orgOguid,
    userOguid,
    locale,
    timezone,
    message: {
      id: messageId,
      role: 'user',
      parts: toolCall
        ? [
            {
              type: 'tool',
              payload: {
                toolName: toolCall.toolName,
                toolArguments: toolCall.toolArguments,
              },
            },
          ]
        : [
            {
              type: 'text',
              text: text || '',
            },
          ],
    },
    client: {
      platform: CLIENT_PLATFORM,
      appVersion: APP_VERSION,
    },
  };
  const assistantHeaders = await getAiAssistantRequestHeaders(userOguid, correlationId);

  const response = await fetch(getAiAssistantChatkitUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...assistantHeaders,
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.text();

    throw new Error(`Assistant request failed: ${response.status} ${response.statusText}${errorBody ? ` ${errorBody}` : ''}`);
  }

  if (!response.body) {
    throw new Error('Assistant request did not return a readable stream');
  }

  await consumeSse(response.body, onEvent, signal);
};

export const getAiAssistantDownloadUrl = (downloadPath: string): string => {
  if (/^https?:\/\//.test(downloadPath)) {
    return downloadPath;
  }

  const normalizedDownloadPath = downloadPath.startsWith('/') ? downloadPath : `/${downloadPath}`;

  return getAiAssistantApiUrl(normalizedDownloadPath);
};

export const downloadAiAssistantFile = async (
  downloadPath: string,
  { orgOguid, userOguid, locale, signal }: IAiAssistantHistoryParams,
): Promise<Blob> => {
  const response = await fetch(getAiAssistantDownloadRequestUrl(downloadPath, { orgOguid, userOguid, locale }), {
    headers: {
      Accept: '*/*',
      ...(await getAiAssistantRequestHeaders(userOguid)),
    },
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.text();

    throw new Error(`Assistant file download request failed: ${response.status} ${response.statusText}${errorBody ? ` ${errorBody}` : ''}`);
  }

  return response.blob();
};

export const fetchAiAssistantThreads = async ({
  orgOguid,
  userOguid,
  locale,
  signal,
}: IAiAssistantHistoryParams): Promise<IAiAssistantThread[]> => {
  const response = await fetch(
    getAiAssistantApiUrl('/assistant/threads', getAiAssistantContextQuery({ orgOguid, userOguid, locale })),
    {
      headers: {
        Accept: 'application/json',
        ...(await getAiAssistantRequestHeaders(userOguid)),
      },
      signal,
    },
  );

  const data = await parseJsonResponse(response, 'Assistant threads request failed');

  return normalizeThreads(data);
};

export const fetchAiAssistantThreadMessages = async (
  threadId: string,
  { orgOguid, userOguid, locale, signal }: IAiAssistantHistoryParams,
): Promise<IAiAssistantHistoryMessage[]> => {
  const response = await fetch(
    getAiAssistantApiUrl(
      `/assistant/threads/${encodeURIComponent(threadId)}/messages`,
      getAiAssistantContextQuery({ orgOguid, userOguid, locale }),
    ),
    {
      headers: {
        Accept: 'application/json',
        ...(await getAiAssistantRequestHeaders(userOguid)),
      },
      signal,
    },
  );

  const data = await parseJsonResponse(response, 'Assistant thread messages request failed');

  return normalizeHistoryMessages(data);
};

export const deleteAiAssistantThread = async (
  threadId: string,
  { orgOguid, userOguid, locale, signal }: IAiAssistantHistoryParams,
): Promise<void> => {
  const response = await fetch(
    getAiAssistantApiUrl(`/assistant/threads/${encodeURIComponent(threadId)}`, getAiAssistantContextQuery({ orgOguid, userOguid, locale })),
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...(await getAiAssistantRequestHeaders(userOguid)),
      },
      signal,
    },
  );

  await parseJsonResponse(response, 'Assistant thread delete request failed');
};

export const cancelAiAssistantRun = async (
  runId: string,
  { orgOguid, userOguid, locale, signal }: IAiAssistantHistoryParams,
): Promise<void> => {
  const response = await fetch(
    getAiAssistantApiUrl(
      `/assistant/runs/${encodeURIComponent(runId)}/cancel`,
      getAiAssistantContextQuery({ orgOguid, userOguid, locale }),
    ),
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...(await getAiAssistantRequestHeaders(userOguid)),
      },
      signal,
    },
  );

  await parseJsonResponse(response, 'Assistant run cancellation request failed');
};

const getAiAssistantChatkitUrl = (): string => {
  return getAiAssistantApiUrl('/chatkit');
};

const getAiAssistantDownloadRequestUrl = (
  downloadPath: string,
  query: Pick<IAiAssistantHistoryParams, 'orgOguid' | 'userOguid' | 'locale'>,
): string => {
  const contextQuery = getAiAssistantContextQuery(query);

  if (/^https?:\/\//.test(downloadPath)) {
    const url = new URL(downloadPath);

    Object.entries(contextQuery).forEach(([key, value]) => {
      if (value && !url.searchParams.has(key)) {
        url.searchParams.append(key, value);
      }
    });

    return url.toString();
  }

  const normalizedDownloadPath = downloadPath.startsWith('/') ? downloadPath : `/${downloadPath}`;

  return getAiAssistantApiUrl(normalizedDownloadPath, contextQuery);
};

const getAiAssistantContextQuery = ({
  orgOguid,
  userOguid,
  locale,
}: Pick<IAiAssistantHistoryParams, 'orgOguid' | 'userOguid' | 'locale'>): Record<string, string | null | undefined> => ({
  orgOguid,
  userOguid,
  locale,
});

const getAiAssistantApiUrl = (path: string, query?: Record<string, string | null | undefined>): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const queryString = getQueryString(query);

  if (process.env.REACT_APP_AI_ASSISTANT_CHATKIT_URL) {
    return `${normalizeAssistantBaseUrl(AI_ASSISTANT_CHATKIT_URL)}${normalizedPath}${queryString}`;
  }

  if (isDevelopment && isLocalDevServer) {
    return `/_ai-assistant${normalizedPath}${queryString}`;
  }

  const assistantUrl = cookies.get(aiAssistantUrlCookieName);

  return `${normalizeAssistantBaseUrl(assistantUrl || AI_ASSISTANT_DEFAULT_URL)}${normalizedPath}${queryString}`;
};

const normalizeAssistantBaseUrl = (url: string): string =>
  url
    .trim()
    .replace(/\/chatkit\/?$/, '')
    .replace(/\/$/, '');

const getQueryString = (query?: Record<string, string | null | undefined>): string => {
  if (!query) return '';

  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value) {
      params.append(key, value);
    }
  });

  const queryString = params.toString();

  return queryString ? `?${queryString}` : '';
};

const getAiAssistantRequestHeaders = async (userOguid: string, correlationId = uuidv4()): Promise<Record<string, string>> => {
  const accessToken = await getAiAssistantAccessToken(userOguid);

  if (!accessToken) {
    throw new Error('Assistant request failed: access token is missing');
  }

  return {
    'Access-Token': accessToken,
    'Correlation-Id': correlationId,
  };
};

const getAiAssistantAccessToken = async (userOguid: string): Promise<string> => {
  const accessToken = TokensService.getTokenFromCookies(BrowserStorage.TOKEN_ACCESS, userOguid);

  if (accessToken) return accessToken;

  const refreshToken = TokensService.getTokenFromCookies(BrowserStorage.TOKEN_REFRESH, userOguid);

  if (!refreshToken) return '';

  await TokensService.memoized(refreshToken);

  return TokensService.getTokenFromCookies(BrowserStorage.TOKEN_ACCESS, userOguid) || '';
};

const parseJsonResponse = async (response: Response, errorMessage: string): Promise<unknown> => {
  if (!response.ok) {
    const errorBody = await response.text();

    throw new Error(`${errorMessage}: ${response.status} ${response.statusText}${errorBody ? ` ${errorBody}` : ''}`);
  }

  if (response.status === 204) return null;

  const responseBody = await response.text();

  return responseBody ? JSON.parse(responseBody) : null;
};

const normalizeThreads = (data: unknown): IAiAssistantThread[] => {
  const items = getArrayPayload(data, ['items', 'threads']);

  return items.reduce<IAiAssistantThread[]>((threads, item) => {
    if (!isRecord(item)) return threads;

    const id = getStringField(item, ['id', 'threadId', 'oguid']);

    if (!id) return threads;

    const title = getStringField(item, ['title', 'name']) || id;
    const createdAt = getStringField(item, ['createdAt', 'created_at']);
    const updatedAt = getStringField(item, ['updatedAt', 'updated_at', 'lastMessageAt', 'last_message_at', 'date']);

    return [
      ...threads,
      {
        id,
        title,
        createdAt: createdAt || undefined,
        updatedAt: updatedAt || undefined,
      },
    ];
  }, []);
};

const normalizeHistoryMessages = (data: unknown): IAiAssistantHistoryMessage[] => {
  const items = getArrayPayload(data, ['items', 'messages']);

  return items.reduce<IAiAssistantHistoryMessage[]>((messages, item) => {
    if (!isRecord(item)) return messages;

    const role = item.role === 'assistant' ? 'assistant' : item.role === 'user' ? 'user' : null;

    if (!role) return messages;

    const id = getStringField(item, ['id', 'messageId', 'oguid']) || `${role}-${messages.length}`;
    const parts = Array.isArray(item.parts) ? item.parts : [];
    const suggestedActions = Array.isArray(item.suggestedActions) ? item.suggestedActions : [];

    return [
      ...messages,
      {
        id,
        role,
        text: typeof item.text === 'string' ? item.text : '',
        parts,
        suggestedActions,
        metadata: isRecord(item.metadata) ? item.metadata : undefined,
      },
    ];
  }, []);
};

const getArrayPayload = (data: unknown, keys: string[]): unknown[] => {
  if (Array.isArray(data)) return data;
  if (!isRecord(data)) return [];

  for (const key of keys) {
    if (Array.isArray(data[key])) {
      return data[key] as unknown[];
    }
  }

  return [];
};

const getStringField = (data: Record<string, unknown>, keys: string[]): string => {
  for (const key of keys) {
    if (typeof data[key] === 'string') {
      return data[key] as string;
    }
  }

  return '';
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const consumeSse = async (
  stream: ReadableStream<Uint8Array>,
  onEvent: (event: IAiAssistantStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let isReading = true;

  while (isReading) {
    if (signal?.aborted) {
      throw new DOMException('The user aborted the request', 'AbortError');
    }

    const { value, done } = await reader.read();

    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    buffer = dispatchBuffer(buffer, onEvent);

    if (done) {
      const trailing = buffer.trim();

      if (trailing) {
        dispatchBlock(trailing, onEvent);
      }

      isReading = false;
    }
  }
};

const dispatchBuffer = (buffer: string, onEvent: (event: IAiAssistantStreamEvent) => void): string => {
  let normalized = buffer.replace(/\r\n/g, '\n');
  let separatorIndex = normalized.indexOf('\n\n');

  while (separatorIndex >= 0) {
    const block = normalized.slice(0, separatorIndex).trim();

    if (block) {
      dispatchBlock(block, onEvent);
    }

    normalized = normalized.slice(separatorIndex + 2);
    separatorIndex = normalized.indexOf('\n\n');
  }

  return normalized;
};

const dispatchBlock = (block: string, onEvent: (event: IAiAssistantStreamEvent) => void): void => {
  const lines = block.split('\n');
  let eventName = 'message';
  const dataLines: string[] = [];

  lines.forEach((line) => {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();

      return;
    }

    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  });

  const rawData = dataLines.join('\n');
  const data = rawData ? parseEventData(rawData) : {};

  onEvent({ event: eventName, data });
};

const parseEventData = (rawData: string): unknown => {
  try {
    return JSON.parse(rawData);
  } catch {
    return rawData;
  }
};
